/**
 * §3.1 and §3.3: when aanmelden is possible and for whom, and the mail that announces it.
 * i18next pluralises the counts, so a language with more than two plural forms needs no markup.
 */
export const inschrijfperiodes = {
  /** The picker both namens screens choose their window with. */
  kiezer: {
    label: "Inschrijfperiode",
    gesloten: "{{naam}} (gesloten)",
  },

  venster: {
    open: "Open tot {{moment}}",
    opent: "Opent op {{moment}}",
    gesloten: "Gesloten sinds {{moment}}",
    badgeOpen: "Open",
    badgeGesloten: "Gesloten",
  },

  doelgroep: {
    korpsen: "Korpsen",
    ondersteuners: "Ondersteuners",
  },

  lijst: {
    label: "Inschrijfperiodes",
    periode: "Inschrijfperiode",
    status: "Status",
    filterDoelgroep: "Doelgroep",
    filterStatus: "Status",
    zoek: "Zoek op naam, doelgroep of status",
    leeg: "Dit seizoen heeft nog geen inschrijfperiodes.",
    terug: "Alle inschrijfperiodes",
    kies: "Kies een inschrijfperiode om die te wijzigen, of maak er een aan.",
    toevoegen: "Inschrijfperiode toevoegen",
    aanSeizoen: "Aan seizoen {{naam}}.",
    submitToevoegen: "Toevoegen",
  },

  form: {
    naam: "Naam",
    naamPlaceholder: "Inschrijving korpsen",
    doelgroep: "Doelgroep",
    opent: "Opent op",
    sluit: "Sluit op",
    wedstrijddagen: "Wedstrijddagen",
    geenDagen: "Dit seizoen heeft nog geen wedstrijddagen om toe te voegen.",
    geenOpenstelbareDagen:
      "Nog geen enkele wedstrijddag heeft een organiserend korps. Je kunt daarom nog geen dag openstellen.",
    zonderKorps_one:
      "Eén wedstrijddag wacht nog op een organiserend korps en staat er daarom niet bij.",
    zonderKorps_other:
      "{{count}} wedstrijddagen wachten nog op een organiserend korps en staan er daarom niet bij.",
    dagRegel: "{{niveau}} · {{korps}}",
    /* On every korpsperiode: the model cannot say which one is a finale, because a niveau is
       renameable and its SortOrder orders nothing but the list (ADR-0029, ADR-0058). */
    promoties:
      "Gaat deze periode over een halve finale of finale? Laat de inschrijfperiode dan gesloten en kondig de periode niet aan. De geplaatste ploegen markeer je op de planning van de wedstrijddag ervoor. De Wedstrijdplanner schrijft ze dan namens hun korps in. Zie Promoties in de handleiding.",
    klassen: "Klassen",
    klassenUitleg:
      "Twee periodes mogen dezelfde wedstrijddag noemen. De klassen houden de periodes uit elkaar. Zo staat Brandbestrijding los van de andere klassen op één dag.",
    voorkeuren: "Voorkeursdatums",
    voorkeurenAan: "Vraag om voorkeursdatums",
    geenVoorkeurAan: "Vraag om datums bij Liever niet",
    maxVoorkeuren: "Hoeveel voorkeursdatums per ploeg",
    maxGeenVoorkeuren: "Hoeveel datums bij Liever niet per ploeg",
    /* Dutch has no "zero" plural, so the empty case is its own key. */
    voorkeurenGeenDagen:
      "Kies eerst wedstrijddagen hierboven. Zonder dagen kun je hier niets kiezen.",
    voorkeurenTeHoog_one: "Je koos één wedstrijddag, dus hier kan hoogstens 1.",
    voorkeurenTeHoog_other:
      "Je koos {{count}} wedstrijddagen, dus hier kan hoogstens {{count}}.",
    geenVoorkeurGeenDagen:
      "Kies eerst wedstrijddagen hierboven. Er moet altijd één dag overblijven.",
    geenVoorkeurTeHoog_one:
      "Er moet één wedstrijddag overblijven, dus hier kan hoogstens 1.",
    geenVoorkeurTeHoog_other:
      "Er moet één wedstrijddag overblijven, dus hier kan hoogstens {{count}}.",
    voorkeurenUitleg:
      "Deze aantallen gelden per ploeg, niet per korps. De ploegen van één korps kunnen op verschillende wedstrijddagen staan. Kies eerst de wedstrijddagen hierboven. Je kunt niet meer antwoorden vragen dan er dagen zijn. Bij Liever niet blijft er altijd één dag over. Zet een vraag uit als je die in deze periode niet stelt. Het aantal blijft dan staan voor als je de vraag weer aanzet. Hebben korpsen meer ingevuld dan het nieuwe aantal? Pas die ploegen dan eerst aan namens het korps.",
  },

  detail: {
    aangekondigd: "Aangekondigd",
    heropenen:
      "Zet <strong>Sluit op</strong> naar een later moment om de periode te heropenen. De nieuwe periode geldt meteen voor iedereen en wordt vastgelegd. Wil je één korps alsnog binnenlaten? Schrijf dan in namens dat korps.",
    verwijderenTitle: "Inschrijfperiode verwijderen",
    verwijderenUitleg:
      "Verwijderen kan zolang niemand zich in deze periode heeft ingeschreven. Daarna verzet je de datums in plaats van de periode te verwijderen. De periode bewaart wat de sluitingsdatum was.",
    verwijderenVraag: "Inschrijfperiode {{naam}} verwijderen?",
    verwijderenDescription:
      "De wedstrijddagen en klassen zelf blijven bestaan. Je kunt dit niet ongedaan maken.",
    verwijderenConfirm: "Inschrijfperiode verwijderen",
  },

  /** §3.3. Never automatic: "the data is ready" is the trigger, and only a human knows that. */
  aankondiging: {
    title: "Aankondiging",
    nogNiet:
      "Nog niet aangekondigd. Kondig pas aan als de wedstrijddagen, banen en klassen kloppen. De mail zegt dat het inschrijven klaarstaat, en niet alleen dat de inschrijfperiode open is.",
    /** The count is the confirmation, and the cheapest possible check that the sync worked. */
    bereik_one:
      "De aankondiging bereikt nu <strong>{{count}}</strong> ontvanger onder de {{doelgroep}}.",
    bereik_other:
      "De aankondiging bereikt nu <strong>{{count}}</strong> ontvangers onder de {{doelgroep}}.",
    gesloten:
      "Deze inschrijfperiode is gesloten. Wie deze mail nu krijgt, kan zich niet meer inschrijven. Zet daarom eerst Sluit op naar een later moment.",
    aankondigen: "Aankondigen",
    bevestigTitle_one: "{{count}} mail versturen?",
    bevestigTitle_other: "{{count}} mails versturen?",
    bevestigDescription:
      "Iedereen onder de {{doelgroep}} krijgt bericht dat {{periode}} openstaat. Het versturen duurt enkele minuten. Na het bevestigen kun je de mails niet meer tegenhouden.",
    bevestigActie_one: "{{count}} mail versturen",
    bevestigActie_other: "{{count}} mails versturen",
    geenOntvangers:
      "Geen ontvangers gevonden. Controleer of de synchronisatie met het JBNL Portaal gelukt is voordat je aankondigt.",
    aangekondigdOp: "Aangekondigd op <strong>{{moment}}</strong>.",
    nuBereik_one:
      "Nu valt {{count}} ontvanger onder de {{doelgroep}}. Is dat aantal anders dan het aantal verstuurde mails? Dan is er sindsdien iets veranderd in het JBNL Portaal.",
    nuBereik_other:
      "Nu vallen {{count}} ontvangers onder de {{doelgroep}}. Is dat aantal anders dan het aantal verstuurde mails? Dan is er sindsdien iets veranderd in het JBNL Portaal.",
    /** Named for what it does: a sent mail is gone, and the stamp only stops a second send. */
    opnieuw: "Opnieuw aankondigen",
    opnieuwTitle_one: "Nog een keer mailen, naar {{count}} ontvanger?",
    opnieuwTitle_other: "Nog een keer mailen, naar {{count}} ontvangers?",
    opnieuwDescription:
      "De mails van {{moment}} zijn verstuurd en blijven staan. Je haalt ze hiermee niet terug. Iedereen onder de {{doelgroep}} krijgt een <strong>tweede</strong> mail, ook wie de eerste gewoon ontvangen heeft.",
    onherroepelijk:
      "Een verstuurde mail is niet terug te halen. Deze knop verstuurt een nieuwe ronde bovenop de vorige.",
    geenMails: "Er staan geen mails onder deze aankondiging.",
    nietAangekomen: "Niet aangekomen",
    /** Follows the address, which the markup renders bold in front of it. */
    probleem: ": {{status}}",
    probleemReden: ": {{status}} ({{reden}})",
    portaalHint:
      "Een onbestelbaar adres corrigeer je in het JBNL Portaal, niet hier. De volgende synchronisatie neemt het nieuwe adres over.",
  },

  /** JBNL's wording for each state the mail queue reports. */
  mailStatus: {
    queued: "In de wachtrij",
    sent: "Verstuurd",
    failed: "Mislukt",
    delivered: "Afgeleverd",
    bounced: "Onbestelbaar",
    complained: "Als spam gemarkeerd",
    onbekend: "Onbekend",
  },

  ontvangers: {
    tonen: "Ontvangers tonen",
    leeg: "Er is nu niemand in deze doelgroep. Controleer of de synchronisatie met het JBNL Portaal gelukt is.",
    zoek: "Zoek op naam, korps of adres",
    allesGetoond: "Alle getoonde selecteren",
    naam: "Naam",
    korps: "Korps",
    email: "E-mailadres",
    laatsteMail: "Laatste mail",
    nogGeenMail: "Nog geen mail",
    laatsteMailMoment: "{{status}} · {{moment}}",
    selecteer: "{{naam}} selecteren",
    geenTreffer: "Geen ontvanger past bij “{{term}}”.",
    gesloten:
      "De inschrijving is gesloten. Je kunt daarom geen herinnering meer sturen.",
    herinneren: "Herinnering sturen ({{count}})",
    herinnerTitle_one: "{{count}} herinnering versturen?",
    herinnerTitle_other: "{{count}} herinneringen versturen?",
    herinnerDescription:
      "Deze mensen krijgen een tweede mail over {{periode}}. Wie zich al heeft ingeschreven, krijgt die mail ook. De selectie gaat over wie een mail kreeg, en niet over wie geantwoord heeft.",
    herinnerActie_one: "{{count}} mail versturen",
    herinnerActie_other: "{{count}} mails versturen",
  },

  errors: {
    ophalen: "Kon de inschrijfperiodes niet ophalen.",
    aanmaken: "Aanmaken is mislukt.",
    opslaan: "Opslaan is mislukt.",
    verwijderen: "Verwijderen is mislukt.",
    aankondigingOphalen: "Kon de aankondiging niet ophalen.",
    aankondigen: "Aankondigen is mislukt.",
    intrekken: "Intrekken is mislukt.",
    ontvangersOphalen: "Kon de ontvangers niet ophalen.",
    herinneren: "Herinnering versturen is mislukt.",
  },
} as const;
